// ═════════════════════════════════════════════════════════════════════════════
//  TIME CLOCK — punches in, hours out
// ═════════════════════════════════════════════════════════════════════════════
// Pure: no DOM, no Supabase, no Date.now(). Every rule value comes from
// settingsFrom(org); 0 = that OT rule is off.
//
// The break and overtime rules are the org's (organizations.clock_* / ot_* /
// stat_holidays), never constants in the math. The numbers below are only what
// an org falls back to when its row — or a localStorage copy of it from before
// those columns existed — does not carry them: BC's Employment Standards Act.
// The one exception is the break mode: every company starts on 'none' (No
// deduction) and turns the automatic deduction on itself (owner decision
// 2026-10-21; organizations.clock_break_mode defaults to 'none' the same way).

import { weekDatesFor } from './field-guards.js';
import { localDateStr } from './period.js';

export const DEFAULT_SETTINGS = Object.freeze({
  breakMode: 'none',               // 'none' | 'auto' | 'manual'   <- organizations.clock_break_mode (No deduction until the company turns it on)
  breakMinutes: 30,                //                               <- clock_break_minutes
  breakAfterHours: 5,              //                               <- clock_break_after_hours
  otDailyAfter: 8,                 // 0 = rule off                  <- ot_daily_after
  otDoubleAfter: 12,               // 0 = rule off                  <- ot_double_after
  otWeeklyAfter: 40,               // 0 = rule off                  <- ot_weekly_after
  gpsFlagM: 500,                   //                               <- clock_gps_flag_m
  statHolidays: Object.freeze([]), // ['YYYY-MM-DD']                <- stat_holidays
  dayHours: 8,                     // a paid day's length           <- paid_sick_hours
  skewMinutes: 5,                  // constant, not a column
  longShiftHours: 14,              // constant, not a column
});

// The settings the math reads, from whatever the caller has: window.currentOrg
// (the camelCase shape of js/core/org.js), a raw organizations row (snake_case),
// or nothing at all. A value that is missing or unreadable (null, blank text,
// false, an array, a negative, 'abc') gets the default: it must not read as 0,
// which would switch that overtime rule OFF and quietly underpay. Only a real
// 0 (the number or '0') means off. Never throws.
export function settingsFrom(org) {
  const o = org ?? {};
  const D = DEFAULT_SETTINGS;
  const pick = (camel, snake) => o[camel] ?? o[snake];   // `??` skips null too
  const num = (camel, snake, dflt) => {
    // Number('') and Number(false) are 0, so only a number or non-blank text
    // is converted; numeric text ('44', ' 12 ') is fine.
    const v = pick(camel, snake);
    const n = (typeof v === 'string' ? v.trim() !== '' : typeof v === 'number') ? Number(v) : NaN;
    return Number.isFinite(n) && n >= 0 ? n : dflt;
  };
  const holidays = pick('statHolidays', 'stat_holidays');
  const mode = pick('clockBreakMode', 'clock_break_mode');
  const dayHours = num('paidSickHours', 'paid_sick_hours', D.dayHours);
  return {
    breakMode: ['none', 'auto', 'manual'].includes(mode) ? mode : D.breakMode,
    breakMinutes: num('clockBreakMinutes', 'clock_break_minutes', D.breakMinutes),
    breakAfterHours: num('clockBreakAfterHours', 'clock_break_after_hours', D.breakAfterHours),
    otDailyAfter: num('otDailyAfter', 'ot_daily_after', D.otDailyAfter),
    otDoubleAfter: num('otDoubleAfter', 'ot_double_after', D.otDoubleAfter),
    otWeeklyAfter: num('otWeeklyAfter', 'ot_weekly_after', D.otWeeklyAfter),
    gpsFlagM: num('clockGpsFlagM', 'clock_gps_flag_m', D.gpsFlagM),
    statHolidays: Array.isArray(holidays) ? [...holidays] : [],
    dayHours: dayHours > 0 ? dayHours : D.dayHours,       // a 0-hour paid day is no day
    skewMinutes: D.skewMinutes,
    longShiftHours: D.longShiftHours,
  };
}

// Absent = on: an org that never touched the toggle (and a cached org from
// before it existed) has the time clock.
export function enabled(org) {
  return org?.features?.time_clock !== false;
}

// Hours to one decimal, the unit a timesheet cell holds. Round each cell, then
// add the cells, and the row total still equals the sum of what is on screen.
export function round1(h) {
  const r = Math.round(h * 10) / 10;
  return Number.isFinite(r) ? r + 0 : 0;   // + 0 turns -0 into 0
}

// ── Geometry and time ───────────────────────────────────────────────────────

// Metres between two points (haversine, R = 6 371 000 m). null unless all four
// are real numbers: a punch with no GPS fix must not read as 0 m = "on site".
// The asin form, clamped, stays a number at the antipodes; atan2(sqrt(a), sqrt(1-a))
// returns NaN there on rounding, and NaN would read as "near".
export function distanceM(lat1, lng1, lat2, lng2) {
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return null;
  const rad = Math.PI / 180;
  const a = Math.sin((lat2 - lat1) * rad / 2) ** 2
    + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin((lng2 - lng1) * rad / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(a)));
}

// The job site: the project's own coordinates, else its mother project's (a
// sub-project stands on the same ground), else null. Both numbers or neither.
export function siteOf(project, projectsById) {
  const coords = p => Number.isFinite(p?.site_lat) && Number.isFinite(p?.site_lng)
    ? { lat: p.site_lat, lng: p.site_lng } : null;
  return project ? coords(project) ?? coords(projectsById?.[project.parent_id]) : null;
}

// Was the punch made farther from the site than the org allows? The phone's own
// GPS error is taken off the distance first, so a weak fix does not flag an
// honest worker. No site or no usable fix: nothing to compare, never flagged.
export function isFar(punch, site, gpsFlagM) {
  if (!site || punch?.lat == null || punch?.lng == null) return false;
  const d = distanceM(punch.lat, punch.lng, site.lat, site.lng);
  return d !== null && d - (punch.gps_accuracy_m || 0) > gpsFlagM;
}

// new Date(null) is 1970-01-01: a valid date, and wrong for "missing". Missing
// and unreadable both come out as an Invalid Date (getTime() is NaN).
const toDate = ts => new Date(ts ?? NaN);

// received_at - sent_at in minutes: how far the phone's clock was from the
// server's when the punch arrived (negative = the server saw it first). null
// when either stamp is missing or unreadable.
export function skewMinutes(punch) {
  const m = (toDate(punch?.received_at) - toDate(punch?.sent_at)) / 60000;
  return Number.isFinite(m) ? m : null;
}

// 'H:MM', 24 h, local time, no leading zero on the hour: 7:02, 15:31, 0:05.
export function fmtTime(ts) {
  const d = toDate(ts);
  return Number.isNaN(d.getTime()) ? '' : d.getHours() + ':' + String(d.getMinutes()).padStart(2, '0');
}

// Whole minutes, like a stopwatch (59 min 59 s is still 59 min): '3 h 41 min',
// '41 min'. Not a number, or below 0: '0 min'.
export function fmtDuration(ms) {
  const min = Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 60000) : 0;
  return min >= 60 ? Math.floor(min / 60) + ' h ' + (min % 60) + ' min' : min + ' min';
}

// The seven ISO dates (Mon..Sun) of the week holding the given date: the
// field-guards.js local-date math under the contract's name.
export { weekDatesFor as weekDates };

// ── Which punches count ─────────────────────────────────────────────────────

// The punches that still count, oldest first. Gone: every void, and every
// punch some other row points at through replaces_id (the target of a void, or
// the earlier link of a correction chain), so a chain leaves only its head, the
// latest correction. The same rule as time_punches_effective() in the database,
// except that the database sees every row and this sees only the rows it is
// given: pass a replacement together with the row it replaces (a void or a
// correction may carry another work_date than its target, so do not cut the
// rows by day first). A punch given twice (a server row and its outbox copy
// share one id) counts once, the first copy; a row without an id is never a
// copy. A new array: the caller's keeps its rows and its order. Punches at the
// same instant come in a fixed order, in, break_start, break_end, out, however
// the rows arrive (a zero-length break is two rows with one punched_at, a break
// may be stored at the very instant of its shift's in or out, and the database
// hands same-instant rows back in any order); rows of one kind keep their input
// order (the sort is stable). Nothing at all: [].
export function effectivePunches(punches) {
  const rows = punches ?? [];
  const gone = new Set(rows.map(p => p.replaces_id).filter(Boolean));   // an ordinary row's null points at nothing
  const seen = new Set();
  const kept = [];
  for (const p of rows) {
    if (p.kind === 'void' || gone.has(p.id) || seen.has(p.id)) continue;
    if (p.id) seen.add(p.id);   // never an empty id: two rows without one are not copies of each other
    kept.push(p);
  }
  // ponytail: a fixed rank at one instant, a total order, so no arrival order can change the day: a break
  // stored at the instant of its in or of its out sits inside the shift, and a zero-length break or shift
  // reads in order. The price: an out followed by an in at ONE instant on the SAME job (a zero-gap split,
  // only a hand-made correction) reads in-first too (dup_in + dup_out, both flagged); a Move (out A + in B)
  // is two jobs and is unaffected, but then the last row of the flat list is not "the latest punch". Upgrade
  // path: read each same-instant run with the shift state (out first while a shift is open).
  const rank = { in: 0, break_start: 1, break_end: 2, out: 3 };
  return kept.sort((a, b) =>
    toDate(a.punched_at) - toDate(b.punched_at) || (rank[a.kind] ?? 4) - (rank[b.kind] ?? 4));   // ISO text, Date or ms: compared as instants
}

// ── One worker, one job, one day ────────────────────────────────────────────

// A punch's instant as ISO text: text as the row carries it (its own offset
// kept), a Date or ms written out, unreadable → null (toISOString() throws).
const isoOf = ts => typeof ts === 'string' ? ts
  : Number.isNaN(toDate(ts).getTime()) ? null : toDate(ts).toISOString();

// The hours of ONE worker on ONE project on ONE work_date (the caller groups;
// an out or a break carries the work_date of the in it closes, so a night shift
// is one group), or null when no punch counts. The punches are walked in time
// order (spec §8, degenerate sequences): an in while a shift is open and an
// out with none open are ignored and flagged dup_in / dup_out (two phones); a
// break_start counts only inside a shift with no break open, a break_end only
// with one open, and a break still open at the out ends at the out and raises
// breakClosedAtOut (a lost "end break" tap must not take the afternoon unseen).
// longShift: one shift longer than longShiftHours from in to out, breaks
// included (spec §8: a shift over 14 h). Hours are instant differences, so a
// DST night is its real length. An open shift counts 0 h and is never long.
// The break rule (applyBreakRule) here sees only this job-day's shifts: the
// hours a screen shows come from weekFromPunches, which runs it over every job
// and date of the worker — a job move does not restart the clock.
// regular / ot15 / ot2 stay null: overtime needs the week (weekFromPunches).
export function dayFromPunches(punches, settings, site = null) {
  // Run again even after a week-wide pass: within this group it puts a
  // same-instant break pair back in start-then-end order.
  const eff = effectivePunches(punches);
  if (!eff.length) return null;
  const ms = p => toDate(p.punched_at).getTime();
  const shifts = [];
  let shift = null, brk = null;   // the open shift { in, pairMs, hasPair } / the open break_start
  let dupIn = false, dupOut = false, breakClosedAtOut = false, longShift = false;
  const endBreak = p => { shift.pairMs += ms(p) - ms(brk); shift.hasPair = true; brk = null; };
  // One closed shift: its instants and its recorded pairs as whole minutes (the pairs' sum,
  // rounded, capped at the shift's own minutes rounded the same way, so only a rule's fixed
  // minutes are ever cut, never a real pair). breakMin and worked are applyBreakRule's: it
  // runs over this job-day's shifts below, and again in weekFromPunches over every job and
  // date of the worker, where a shift may chain into one of another job or day (spec §8,
  // amended 2026-10-21: a job move does not restart the break clock).
  const closeShift = out => {
    if (brk) { endBreak(out); breakClosedAtOut = true; }
    const grossMs = ms(out) - ms(shift.in);
    if (grossMs / 3600000 > settings.longShiftHours) longShift = true;
    shifts.push({ start: fmtTime(shift.in.punched_at), end: fmtTime(out.punched_at),
      startAt: isoOf(shift.in.punched_at), endAt: isoOf(out.punched_at),
      pairMin: shift.hasPair ? Math.min(Math.round(shift.pairMs / 60000), Math.round(grossMs / 60000)) : null,
      breakMin: 0, open: false, worked: 0 });
    shift = null;
  };
  for (const p of eff) {
    if (p.kind === 'in') { if (shift) dupIn = true; else shift = { in: p, pairMs: 0, hasPair: false }; }
    else if (p.kind === 'out') { if (shift) closeShift(p); else dupOut = true; }
    else if (p.kind === 'break_start') { if (shift && !brk) brk = p; }
    else if (p.kind === 'break_end') { if (brk) endBreak(p); }
  }
  if (shift) shifts.push({ start: fmtTime(shift.in.punched_at), end: null,
    startAt: isoOf(shift.in.punched_at), endAt: null, pairMin: null, breakMin: 0, open: true, worked: 0 });

  const first = shifts[0], last = shifts[shifts.length - 1];
  const inOut = eff.filter(p => p.kind === 'in' || p.kind === 'out');
  let farM = null;   // metres the farthest in/out stood past its own GPS error
  for (const p of inOut) {
    const d = site ? distanceM(p.lat, p.lng, site.lat, site.lng) : null;
    if (d !== null) farM = Math.max(farM ?? -Infinity, d - (p.gps_accuracy_m || 0));
  }
  const fixes = eff.filter(p => p.source === 'correction');
  const sources = [...new Set(eff.filter(p => p.source !== 'correction').map(p => p.source))];
  const day = {
    workerId: eff[0].worker_id, projectId: eff[0].project_id, date: eff[0].work_date,
    worked: 0, regular: null, ot15: null, ot2: null,
    start: first ? first.start : null, end: last ? last.end : null,
    startAt: first ? first.startAt : null, endAt: last ? last.endAt : null,
    breakMin: 0, breakAuto: false, shifts,
    open: shift !== null, onBreakSince: brk ? isoOf(brk.punched_at) : null,
    far: inOut.some(p => isFar(p, site, settings.gpsFlagM)), farM,
    corrected: fixes.length > 0, correctedBy: fixes[fixes.length - 1]?.punched_by_name || null,
    late: eff.some(p => p.offline === true),
    skew: eff.some(p => Math.abs(skewMinutes(p) ?? 0) > settings.skewMinutes),
    dup_in: dupIn, dup_out: dupOut, noBreak: false, breakClosedAtOut, longShift,
    source: sources.length === 1 ? sources[0] : sources.length ? 'mixed' : 'crew',   // only corrections: a foreman wrote the day
    rule: { daily: settings.otDailyAfter, double: settings.otDoubleAfter, weekly: settings.otWeeklyAfter },
    punch_ids: eff.map(p => p.id),
    offConflict: null,   // the prefill sets it from approved time off
  };
  return applyBreakRule([day], settings)[0];   // the rule over this job-day alone (see closeShift)
}

// ── The break rule: one worker, every job and date ──────────────────────────

const msOf = iso => toDate(iso).getTime();   // a shift's startAt / endAt as an instant

// The break rule (spec §8 "Pausas", amended 2026-10-21 — W3 Task 0b: a job move does not
// restart the clock; BC counts consecutive hours of work) over ONE worker's DayResults,
// any job and work_date, in place; returns them. Rewrites every shift's breakMin / worked
// and every day's worked, breakMin, breakAuto, noBreak from each shift's pairMin and
// instants, so running it again over a larger set (weekFromPunches over the week, after
// dayFromPunches over one job-day) gives the larger set's answer.
// The shifts, in time order (two starting at one instant: by job), chain into a
// continuous block while the gap from the block's last out to the next in is
// shorter than breakMinutes (a Move — out A and in B at one instant — is a
// 0-minute gap; an open shift is 0 h and ends its block). A longer gap ends
// the block: shorter than breakAfterHours it is that block's break, taken and unpaid
// already, so the block is never deducted or flagged; breakAfterHours or more it is the
// end of the day, and the block is judged on its own. Per block, when no shift of it has a
// pair and its closed shifts' gross hours reach breakAfterHours: the minutes owed are
// breakMinutes less the block's unpaid gap minutes (whole minutes, like fmtDuration);
// 'auto' takes them off the shift in which the block reached breakAfterHours — where the
// break became due, as OT lands on the shift where the hour was punched — spilling to the
// later shifts in time order and then to the earlier ones backwards when that shift is
// shorter, each down to 0 h; 'manual' deducts nothing and flags noBreak on that shift's
// day; 'none' deducts nothing. A pair anywhere in the block wins: every shift loses its own
// pairs as recorded (a 0-minute pair is 0) and the rule adds nothing to the block.
export function applyBreakRule(days, settings) {
  if (new Set(days.map(d => d.workerId)).size > 1) throw new TypeError('applyBreakRule: one worker at a time');
  const { breakMode, breakMinutes, breakAfterHours } = settings;
  const afterMs = breakAfterHours * 3600000, breakMs = breakMinutes * 60000;
  for (const d of days) Object.assign(d, { worked: 0, breakMin: 0, breakAuto: false, noBreak: false });
  const walk = days.flatMap(d => d.shifts.map(s => [s, d, s.open ? 0 : msOf(s.endAt) - msOf(s.startAt)]))   // [shift, its day, gross ms]
    .sort(([a, da], [b, db]) => msOf(a.startAt) - msOf(b.startAt) || (da.projectId > db.projectId) - (da.projectId < db.projectId));   // two shifts at one instant: by job, never by input order
  const blocks = [];
  let block = null, endMs = null;   // the block being built; the instant of its last out (null: none yet, or an open shift)
  for (const row of walk) {
    const [s, , grossMs] = row, startMs = msOf(s.startAt), gap = endMs === null ? null : startMs - endMs;
    if (gap === null || gap >= breakMs) {
      if (block) block.rested = gap !== null && gap < afterMs;   // the gap after the block was its break
      blocks.push(block = { rows: [], gapMs: 0, rested: false });
    } else block.gapMs += Math.max(0, gap);
    block.rows.push(row);
    endMs = s.open ? null : Math.max(endMs ?? -Infinity, startMs + grossMs);
  }
  for (const { rows, gapMs, rested } of blocks) {
    for (const [s] of rows) s.breakMin = s.pairMin ?? 0;
    let cum = 0;
    const k = rows.findIndex(([, , g]) => g > 0 && (cum += g) >= afterMs);   // the shift in which the block reached breakAfterHours; -1: never
    let owed = k >= 0 && !rested && !rows.some(([s]) => s.pairMin !== null) ? Math.max(0, breakMinutes - Math.floor(gapMs / 60000)) : 0;
    if (owed && breakMode === 'manual') rows[k][1].noBreak = true;
    if (owed && breakMode === 'auto') for (const [s, d, g] of [...rows.slice(k), ...rows.slice(0, k).reverse()]) {
      const take = Math.min(owed, Math.round(g / 60000));
      if (take > 0) { s.breakMin = take; d.breakAuto = true; owed -= take; }
      if (!owed) break;
    }
    for (const [s, d, g] of rows) { s.worked = Math.max(0, g / 3600000 - s.breakMin / 60); d.worked += s.worked; d.breakMin += s.breakMin; }
  }
  return days;
}

// The timesheet cell's hint: first in – last out · break minutes taken off
// ('7:02–15:31 · 30m'; no break: '7:02–15:31'), or '7:02 – OPEN' while a shift
// is open. Reads only start, end, breakMin and open, so a cell's copy of those
// works as well as a DayResult. No day, or a day with no check-in: ''.
export function fmtHint(day) {
  if (!day?.start) return '';
  if (day.open) return day.start + ' – OPEN';
  return day.start + '–' + day.end + (day.breakMin ? ' · ' + day.breakMin + 'm' : '');
}

// Whether a clock day holds hours a Weekly Timesheet keeps: closed, and R + OT > 0 as the prefill fills the cell
// (R = round1(regular), OT = round1(ot15 + ot2), contract §4.2). An in and out under 3 minutes apart reads 0 / 0, and
// a signed sheet writes no time_entries row for 0 h (sheetRows): nothing may wait for a row that never comes.
export function hasSheetHours(day) {
  return !!day && !day.open && round1(day.regular) + round1(day.ot15 + day.ot2) > 0;
}

// [flag, the DayResult field that raises it, English label], in flags() order.
const FLAGS = [
  ['open', 'open', 'Open punch'],
  ['far', 'far', 'GPS far'],
  ['corrected', 'corrected', 'Corrected'],
  ['late', 'late', 'Synced later'],
  ['skew', 'skew', 'Clock skew'],
  ['dup_in', 'dup_in', 'Duplicate check-in'],
  ['dup_out', 'dup_out', 'Duplicate check-out'],
  ['no_break', 'noBreak', 'No break taken'],
  ['break_closed_at_out', 'breakClosedAtOut', 'Break never ended — closed at check-out'],
  ['long_shift', 'longShift', 'Long shift'],
  ['off_conflict', 'offConflict', 'Approved time off but punched'],
];

// The flags a day raises, in that order. No day: none.
export function flags(day) {
  return FLAGS.filter(([, field]) => day?.[field]).map(([flag]) => flag);
}

// A flag's English label; a flag outside the list is shown as itself.
export function flagLabel(flag) {
  return FLAGS.find(([f]) => f === flag)?.[2] ?? flag;
}

// ── One worker, one week, every job ─────────────────────────────────────────

// How much of the stretch [a, b) lies inside [lo, hi).
const overlap = (a, b, lo, hi) => Math.max(0, Math.min(b, hi) - Math.max(a, lo));

// Every DayResult of the punches, worker → work_date → project, with regular /
// ot15 / ot2 filled in, and each worker's totals over the clock days. Overtime
// is per person across every job given, and lands on the job of the shift where
// the hour was punched (spec §8, D11). One `if` per rule, 0 = that rule off:
// - break: before any hour is split, applyBreakRule runs over the worker's
//   job-days of the week (contract §2.4): a job move does not restart the break
//   clock.
// - daily: the worker's shifts of the day on every job, in time order; the hours
//   past otDailyAfter are 1.5x, past otDoubleAfter 2x. Daily off: the whole day
//   is regular, no 2x either. An open shift is 0 h.
// - weekly: the dates of opts.monday's week in order (no monday: every date
//   given, sorted — pass one week). On each date, first the hours typed by hand
//   (opts.manualDays), their sum capped at otDailyAfter; then each shift's
//   regular hours in time order, and those past otWeeklyAfter move to ot15 on
//   that shift's job. Only regular hours count toward the cap (BC). A typed row
//   with a project_id stands for that job's clock day: the day keeps its daily
//   split and its hours are not counted a second time. Typed hours never enter
//   the totals.
// - stat holiday (a date in settings.statHolidays): the day stays whole, every
//   hour regular (no daily split, no 2x), and the date is left out of the weekly
//   walk, typed hours included: they neither count toward otWeeklyAfter nor take
//   weekly OT. splitPayroll pays that day as StatHolidayHours outside the 40 h
//   (spec §10), so the prefill never suggests OT the export would not pay.
// Unrounded: round1 is the prefill's, toFixed(2) the CSV's.
export function weekFromPunches(punches, settings, opts = {}) {
  const { otDailyAfter: daily, otDoubleAfter: double, otWeeklyAfter: weekly } = settings;
  const stats = new Set(settings.statHolidays);
  const manual = opts.manualDays ?? [];
  const eff = effectivePunches(punches);
  const groups = {};   // worker → date → project → punches
  for (const p of eff) (((groups[p.worker_id] ??= {})[p.work_date] ??= {})[p.project_id] ??= []).push(p);

  const byWorker = {}, regs = {};   // regs: worker → date → [a shift's regular hours, its DayResult], in time order
  for (const [worker, dates] of Object.entries(groups)) {
    const mine = byWorker[worker] = {}; regs[worker] = {};
    for (const [date, jobs] of Object.entries(dates)) {
      const days = mine[date] = {};
      for (const [project, rows] of Object.entries(jobs))
        days[project] = Object.assign(dayFromPunches(rows, settings, opts.sites?.[project] ?? null), { regular: 0, ot15: 0, ot2: 0 });
    }
    applyBreakRule(Object.values(mine).flatMap(jobs => Object.values(jobs)), settings);   // every job and date of the worker, once
    for (const [date, days] of Object.entries(mine)) {
      // startAt read as an instant, never as text: each keeps its row's own offset ('…-07:00', 'Z', '+00:00')
      const walk = Object.values(days).flatMap(d => d.shifts.map(s => [s, d]))
        .sort(([a], [b]) => toDate(a.startAt) - toDate(b.startAt));
      const pieces = regs[worker][date] = [];
      let cum = 0;   // hours already worked that day, every job
      for (const [{ worked: w }, day] of walk) {
        let regular = w, ot15 = 0, ot2 = 0;
        if (daily && !stats.has(date)) {
          ot2 = double ? overlap(cum, cum + w, double, Infinity) : 0;
          ot15 = overlap(cum, cum + w, daily, double || Infinity);
          regular = w - ot15 - ot2;
        }
        day.regular += regular; day.ot15 += ot15; day.ot2 += ot2;
        pieces.push([regular, day]);
        cum += w;
      }
    }
  }

  if (weekly) {
    const dates = opts.monday ? weekDatesFor(opts.monday) : [...new Set([...eff, ...manual].map(r => r.work_date))].sort();
    for (const [worker, byDate] of Object.entries(regs)) {
      let acc = 0;   // regular hours so far this week
      for (const date of dates) {
        if (stats.has(date)) continue;   // paid apart, outside the 40 h
        const typed = manual.filter(m => m.worker_id === worker && m.work_date === date);
        const typedSum = typed.reduce((h, m) => h + m.hours, 0);
        acc += daily ? Math.min(typedSum, daily) : typedSum;
        const replaced = new Set(typed.map(m => m.project_id).filter(Boolean));
        for (const [r, day] of byDate[date] ?? []) {
          if (replaced.has(day.projectId)) continue;
          const over = Math.min(r, Math.max(0, acc + r - weekly));
          day.regular -= over; day.ot15 += over; acc += r - over;
        }
      }
    }
  }

  const totals = {};
  for (const [worker, dates] of Object.entries(byWorker)) {
    const t = totals[worker] = { worked: 0, regular: 0, ot15: 0, ot2: 0 };
    for (const day of Object.values(dates).flatMap(jobs => Object.values(jobs))) for (const k in t) t[k] += day[k];
  }
  return { byWorker, totals, punches: eff.length };
}

// ── Payroll: one row per employee, from the signed sheets ───────────────────

// The day flags the payroll CSV words in Notes, in flags() order (contract §7);
// every other flag stays out. break_closed_at_out is a deduction nobody tapped.
const NOTE_FLAGS = { far: 'GPS flag', corrected: 'corrected', break_closed_at_out: 'break never ended — closed at check-out' };
const DDD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];   // weekDatesFor's order
const md = date => `${+date.slice(5, 7)}/${+date.slice(8, 10)}`;   // '2026-10-12' → '10/12'
// 'Oct 20': a timestamptz on the admin's own calendar, in English for the bookkeeper.
export const monD = ts => {
  const [y, m, d] = localDateStr(ts).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const byText = (a, b) => a.localeCompare(b);

// What the QuickBooks export prints (spec §10, contract §2.5 / §7), from the
// SIGNED time_entries of the week, never from punches. Overtime is per person
// across every job: each day's worked hours (T, every job) are split by the
// rule, one `if` per rule, 0 = off; the OT the foremen signed is a floor (hours
// only move R → OT); then the weekly cap, the same walk as weekFromPunches. A
// stat day's hours live only in StatHolidayHours (a sick day on a stat date pays the holiday only); paid sick rows (sickKeys =
// their ids) are SickHours; vacation pays an approved day with no hours on the
// sheet, never a stat, once, at the largest day_hours of the requests naming
// it. A row with 0 h (an admin correction) is no hours. Hours on a job whose
// week is not final hold the employee back, by name; hours of a worker missing
// from `workers` are held back as 'Unknown worker', never dropped. On a
// re-export an employee is "new" whenever something of his may have changed
// since exportedAt: a job-week never exported or re-approved after it, time
// off decided after it, an admin edit after it — a missing or unreadable stamp
// counts as after, so a change is never hidden behind "unchanged". version = 1 + the
// highest export_count of the week's finals. Sums run in whole hundredths
// (time_entries hours are numeric(5,2)): 1.3 + 8.3 + 2.4 is 12, not
// 12.000000000000002, so float dust never prints a 0.00 line or a "0.0 h DT";
// out come unrounded hours, W5 formats them.
export function splitPayroll(input) {
  const { settings: s, monday, sunday, entries = [], sickKeys = new Set(), timeOff = [], workers = [], projects = [],
    finals = [], taskNames = {}, dayFlags = {}, correctedBy = {}, adminEdits = {}, exportedAt = null } = input;
  const h100 = v => Math.round((Number(v) || 0) * 100);   // hours → whole hundredths
  const hrs = e => h100(e.regular_hours) + h100(e.overtime_hours);
  const [daily, double, weekly] = [s.otDailyAfter, s.otDoubleAfter, s.otWeeklyAfter].map(h100);
  const dates = weekDatesFor(monday);
  const week = entries.filter(e => dates.includes(e.work_date));
  const jobNames = Object.fromEntries(projects.map(p => [p.id, p.name]));
  const job = id => jobNames[id] ?? '';
  const final = new Map(finals.filter(f => f.status === 'final' && f.week_of === monday).map(f => [f.project_id, f]));
  const approved = timeOff.filter(t => t.status === 'approved');

  const heldBack = [], included = [];
  for (const w of workers) {
    const mine = week.filter(e => e.worker_id === w.id);
    const off = approved.filter(t => t.worker_id === w.id);
    if (!mine.length && !off.some(t => t.days.some(d => dates.includes(d)))) continue;
    const open = [...new Set(mine.filter(e => !final.has(e.project_id)).map(e => job(e.project_id)))];
    if (open.length) heldBack.push({ worker_id: w.id, name: w.name, projects: open.sort(byText) });
    else included.push({ w, mine, off });
  }
  const known = new Set(workers.map(w => w.id));
  for (const id of new Set(week.map(e => e.worker_id))) if (!known.has(id)) heldBack.push({ worker_id: id, name: 'Unknown worker',
    projects: [...new Set(week.filter(e => e.worker_id === id).map(e => job(e.project_id)))].sort(byText) });
  const paid = new Set(included.map(({ w }) => w.id));
  const includedWeeks = [...final.values()].filter(f => week.every(e => e.project_id !== f.project_id || paid.has(e.worker_id)));
  const version = Math.max(0, ...finals.filter(f => f.week_of === monday).map(f => f.export_count || 0)) + 1;
  // an admin edit is its reason (time unknown) or { reason, at }
  const editOf = e => { const v = adminEdits[e.id]; return v == null ? null : typeof v === 'object' ? v : { reason: v }; };
  const sinceExport = ts => ts == null || !(toDate(ts) <= toDate(exportedAt));   // instants; missing or unreadable = after

  const summary = [], detail = [];
  for (const { w, mine, off } of included) {
    const Employee = w.payroll_name || w.name, EmployeeID = w.employee_id || '', ServiceItem = w.trade || 'Labour';
    const line = (e, PayrollItem, h) => ({ worker_id: w.id, project_id: e.project_id, work_date: e.work_date, Employee, EmployeeID,
      Customer: job(e.project_id), ServiceItem, PayrollItem, Hours: h / 100,
      ClockIn: (e.start_time ?? '').slice(0, 5), ClockOut: (e.end_time ?? '').slice(0, 5),
      BreakMinutes: PayrollItem === 'Sick Pay' || PayrollItem === 'Vacation Pay' ? '' : e.break_minutes,
      Description: taskNames[e.task_id] ?? '' });
    const days = dates.map((d, i) => {
      const rows = mine.filter(e => e.work_date === d && hrs(e) > 0);
      const sick = rows.filter(e => sickKeys.has(e.id));
      // by first clock-in, then job; a stable sort, so a re-filed day's task rows keep their order
      const work = rows.filter(e => !sickKeys.has(e.id)).sort((a, b) =>
        (a.start_time ?? '99:99').localeCompare(b.start_time ?? '99:99') || byText(job(a.project_id), job(b.project_id)));
      const T = work.reduce((t, e) => t + hrs(e), 0), sumOt = work.reduce((t, e) => t + h100(e.overtime_hours), 0);
      const stat = s.statHolidays.includes(d);
      let regular = 0, ot15 = 0, ot2 = 0;
      if (!stat) {
        const dbl = daily && double ? Math.max(0, T - double) : 0;   // the 2x rule only with the daily rule on
        regular = T;
        if (daily) { ot2 = dbl; ot15 = Math.max(0, Math.min(T, double || Infinity) - daily); regular = T - ot15 - ot2; }
        if (ot15 + ot2 < sumOt) { ot2 = Math.min(sumOt, dbl); ot15 = sumOt - ot2; regular = T - sumOt; }   // the signed OT is the floor
      }
      return { d, i, key: w.id + '|' + d, rows, sick, work, T, stat, regular, ot15, ot2 };
    });
    let weeklyOt = 0;
    if (weekly) {
      let acc = 0;   // regular hours so far this week
      for (const day of days) {
        const over = Math.min(day.regular, Math.max(0, acc + day.regular - weekly));
        day.regular -= over; day.ot15 += over; acc += day.regular; weeklyOt += over;
      }
    }

    const t = { R: 0, OT: 0, DT: 0, sick: 0, vac: 0, stat: 0, statOff: 0 };
    const f1 = h => (h / 100).toFixed(1);
    const dtDays = [], flagged = [], edits = [], sickOff = [];
    for (const { d, i, key, rows, sick, work, T, stat, regular, ot15, ot2 } of days) {
      if (!stat) for (const e of sick) { t.sick += hrs(e); detail.push(line(e, 'Sick Pay', hrs(e))); }   // a stat day pays the holiday only
      if (stat) {
        t.stat += T;
        for (const e of work) detail.push(line(e, 'Stat Holiday Pay', hrs(e)));
        if (!work.length) t.statOff++;
      } else {
        t.R += regular; t.OT += ot15; t.DT += ot2;
        let cum = 0;   // each row's hours, cut at the day's final R / OT / DT boundaries
        for (const e of work) {
          for (const [item, lo, hi] of [['Regular Pay', 0, regular], ['Overtime Pay', regular, regular + ot15], ['Double Overtime Pay', regular + ot15, T]]) {
            const h = overlap(cum, cum + hrs(e), lo, hi);
            if (h) detail.push(line(e, item, h));
          }
          cum += hrs(e);
        }
        if (ot2) dtDays.push(`${DDD[i]} ${md(d)} worked ${f1(T)} h (${f1(ot2)} h DT)`);
        const vac = rows.length ? 0 : Math.max(0, ...off.filter(o => o.kind === 'vacation' && o.days.includes(d)).map(o => h100(o.day_hours)));
        if (vac) { t.vac += vac; detail.push(line({ project_id: null, work_date: d }, 'Vacation Pay', vac)); }
      }
      const fl = dayFlags[key] || [];
      for (const [f, phrase] of Object.entries(NOTE_FLAGS)) if (fl.includes(f))
        flagged.push(`${DDD[i]} ${phrase}${f === 'corrected' && correctedBy[key] ? ' by ' + correctedBy[key] : ''}`);
      for (const e of mine) { const ed = e.work_date === d && editOf(e); if (ed) edits.push(`${DDD[i]} edited by admin (${ed.reason ?? ''})`); }
      if (!stat && !rows.length && off.some(o => o.kind === 'sick' && o.days.includes(d))) sickOff.push(`approved sick ${md(d)} not on sheet`);
    }
    const isNew = [...new Set(mine.map(e => e.project_id))].map(p => final.get(p))
        .some(f => f.exported_at == null || (f.approved_at && toDate(f.approved_at) > toDate(f.exported_at)))
      || off.some(o => o.days.some(d => dates.includes(d)) && sinceExport(o.decided_at))
      || mine.some(e => editOf(e) && sinceExport(editOf(e).at));
    const notes = [`rule ${s.otDailyAfter || 'off'}/${s.otDoubleAfter || 'off'}/${s.otWeeklyAfter || 'off'}`, ...dtDays, ...flagged, ...edits,
      weeklyOt && `weekly OT ${f1(weeklyOt)} h across ${[...new Set(mine.map(e => job(e.project_id)))].sort(byText).join(' + ')}`,
      days[6].T && `Sun worked ${f1(days[6].T)} h`, ...sickOff,
      exportedAt != null && (isNew ? `new in v${version}` : `unchanged since v${version - 1} ${monD(exportedAt)}`)];
    summary.push({ worker_id: w.id, WeekEnding: sunday, Employee, EmployeeID,
      RegularHours: t.R / 100, OvertimeHours: t.OT / 100, DoubleOvertimeHours: t.DT / 100, SickHours: t.sick / 100,
      VacationHours: t.vac / 100, StatHolidayHours: t.stat / 100, StatDaysNotWorked: t.statOff,
      TotalHours: (t.R + t.OT + t.DT + t.sick + t.vac + t.stat) / 100,
      notes: [...new Set(notes.filter(Boolean))] });   // a re-filed day's task rows share one admin reason
  }
  return { summary, detail, heldBack, includedWeeks: includedWeeks.map(f => ({ project_id: f.project_id, week_of: f.week_of })), version };
}
